import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CalendarDays, CheckCircle2, Clock, Hourglass, Lock, ShieldCheck, Users, XCircle } from 'lucide-react';
import {
  getPaymentLinkSummary,
  isWellFormedPaymentLinkToken,
  payPaymentLink,
  postPaymentLink3ds,
  type PaymentLinkSummary,
} from '../../../api/paymentLinkApi';
import { PortalApiService, resolveThreeDSReturnUrl } from '../../../services/portalApiService';
import { ThreeDSModal } from '../../../components/ThreeDSModal';
import { CreditCardInput } from '../../invitation/components/CreditCardInput';
import { LanguageToggle } from '../../../components/LanguageToggle';
import { Button } from '../../../components/ui/Button';
import { Notice } from '../../../components/ui/Notice';
import { useLang } from '../../../i18n/publicEvents';
import { useTenantBranding } from '../../../hooks/useTenantBranding';
import { useS3Image } from '../../../hooks/useS3Image';
import { usePublicPageScroll } from '../../../hooks/usePublicPageScroll';
import { CARD_FORM_LABELS, payLinkT, type PayLinkStringKey } from './payLinkStrings';
import { PaymentLinkPassCard } from './PaymentLinkPassCard';
import { PayingPanel } from './PayingPanel';
import './payLinkPrint.css';
import {
  EMPTY_CARD,
  ThreeDSChainError,
  buildPayBody,
  cardBrandOf,
  classifyPaymentLinkError,
  formatBusinessDate,
  formatBusinessDateTime,
  formatBusinessTimeRange,
  formatCountdown,
  formatMoney,
  initialPayLinkState,
  isCardFormComplete,
  msRemaining,
  outcomeForPayResponse,
  payLinkReducer,
  phaseForSummary,
  pickText,
  receiptDocumentTitle,
  runPaymentLink3ds,
  toPaymentLinkCard,
  type CardFormValue,
} from './paymentLinkModel';

// SAM-633 — public pay page for a booking payment link: https://<tenant web origin>/pay/<token>.
// No account, no Authorization header, no business context (see paymentLinkApi.ts). Everything
// money-related is the server's: the page shows the stored line items and total and never sends
// or computes a price. Card data lives only in React state for the length of one attempt; it is
// never logged, never written to storage, and is wiped on every result.

const POLL_INTERVAL_MS = 3000;
const POLL_MAX = 20; // ~1 minute of "checking" before settling on "still confirming"
const PROCESSING_POLL_INTERVAL_MS = 6000;
const PROCESSING_POLL_MAX = 20;
/** After the charge we hold "All set!" this long so the last step reads as done before the receipt. */
const FINISH_PAUSE_MS = 700;
/** Past this, step 2 (charge + engine confirmation) says it is taking longer than usual. */
const SLOW_CHARGE_MS = 15000;

/** `<meta name="referrer" content="no-referrer">` while this page is mounted — the URL carries the token. */
function useNoReferrer() {
  useEffect(() => {
    let meta = document.querySelector<HTMLMetaElement>('meta[name="referrer"]');
    const created = !meta;
    const previous = meta?.getAttribute('content') ?? null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'referrer');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'no-referrer');
    return () => {
      if (!meta) return;
      if (created) meta.remove();
      else if (previous !== null) meta.setAttribute('content', previous);
    };
  }, []);
}

type ChallengeParams = { stepUpUrl: string; accessToken: string; authTransactionId: string };

export const PayLinkPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { lang } = useLang();
  const t = useCallback((key: PayLinkStringKey, vars?: Record<string, string | number>) => payLinkT(lang, key, vars), [lang]);
  const { tenant, tenantName } = useTenantBranding();

  useNoReferrer();
  usePublicPageScroll();

  const [state, dispatch] = useReducer(payLinkReducer, initialPayLinkState);
  const [card, setCard] = useState<CardFormValue>(EMPTY_CARD);
  const [now, setNow] = useState(() => Date.now());
  const [challenge, setChallenge] = useState<ChallengeParams | null>(null);
  const challengeResolver = useRef<{ resolve: (tid: string) => void; reject: (e: Error) => void } | null>(null);
  const inFlight = useRef(false);
  const [finishing, setFinishing] = useState(false);
  const [slow, setSlow] = useState(false);

  const summary = state.summary;
  const businessLogoKey = summary?.business?.logo_url || tenant?.settings?.logo;
  const { imageUrl: logoUrl } = useS3Image(businessLogoKey);

  // ── Load ────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!isWellFormedPaymentLinkToken(token)) {
      dispatch({ type: 'OUTCOME', outcome: { kind: 'unavailable' } });
      return;
    }
    dispatch({ type: 'RELOAD' });
    try {
      const data = await getPaymentLinkSummary(token);
      dispatch({ type: 'LOADED', summary: data, now: Date.now() });
    } catch (error) {
      dispatch({ type: 'OUTCOME', outcome: classifyPaymentLinkError(error, 'summary') });
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Wipe card data whenever the reducer says so (every result) ───────────
  useEffect(() => {
    setCard(EMPTY_CARD);
  }, [state.cardResetKey]);

  // ── "Taking longer than usual" once the charge step runs past SLOW_CHARGE_MS ──
  useEffect(() => {
    if (state.phase !== 'submitting' || state.step !== 'charging') {
      setSlow(false);
      return;
    }
    const id = window.setTimeout(() => setSlow(true), SLOW_CHARGE_MS);
    return () => window.clearTimeout(id);
  }, [state.phase, state.step]);

  // ── Countdown ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (state.phase !== 'form' && state.phase !== 'submitting') return;
    const id = window.setInterval(() => {
      const ts = Date.now();
      setNow(ts);
      dispatch({ type: 'TICK', now: ts });
    }, 1000);
    return () => window.clearInterval(id);
  }, [state.phase]);

  // ── Polling: re-read the summary instead of ever re-posting /pay ─────────
  useEffect(() => {
    if (state.phase !== 'polling' && state.phase !== 'processing') return;
    if (!isWellFormedPaymentLinkToken(token)) return;
    const polling = state.phase === 'polling';
    const interval = polling ? POLL_INTERVAL_MS : PROCESSING_POLL_INTERVAL_MS;
    const max = polling ? POLL_MAX : PROCESSING_POLL_MAX;
    let cancelled = false;
    let tries = 0;
    let timer: number | undefined;
    const tick = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const data = await getPaymentLinkSummary(token);
        if (cancelled) return;
        if (phaseForSummary(data) !== state.phase) {
          dispatch({ type: 'LOADED', summary: data, now: Date.now(), fromPoll: true });
          return;
        }
      } catch (error) {
        if (cancelled) return;
        const outcome = classifyPaymentLinkError(error, 'summary');
        if (outcome.kind === 'unavailable') {
          dispatch({ type: 'OUTCOME', outcome });
          return;
        }
      }
      if (tries >= max) {
        if (polling) dispatch({ type: 'POLL_GAVE_UP' });
        return;
      }
      timer = window.setTimeout(tick, interval);
    };
    timer = window.setTimeout(tick, polling ? 800 : interval);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [state.phase, token]);

  // ── 3DS challenge bridge (the shared ThreeDSModal) ──────────────────────
  const openChallenge = useCallback(
    (params: ChallengeParams) =>
      new Promise<string>((resolve, reject) => {
        challengeResolver.current = { resolve, reject };
        setChallenge(params);
      }),
    [],
  );
  const onChallengeComplete = useCallback((transactionId: string) => {
    setChallenge(null);
    challengeResolver.current?.resolve(transactionId);
    challengeResolver.current = null;
  }, []);
  const onChallengeError = useCallback(() => {
    setChallenge(null);
    challengeResolver.current?.reject(new ThreeDSChainError('THREEDS_TIMEOUT'));
    challengeResolver.current = null;
  }, []);

  // ── Pay ─────────────────────────────────────────────────────────────────
  const cardComplete = isCardFormComplete(card);

  const onPay = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (inFlight.current || state.phase !== 'form' || !token) return;
    if (!cardComplete) {
      dispatch({ type: 'OUTCOME', outcome: { kind: 'retry', key: 'err_formIncomplete' } });
      return;
    }
    inFlight.current = true;
    const attemptCard = toPaymentLinkCard(card);
    const cardholderName = card.cardHolder;
    dispatch({ type: 'SUBMIT' });
    try {
      let authData;
      // SAM-1171: a pass link is charged on Cardnet without 3-D Secure; its /3ds answers THREEDS_NOT_REQUIRED.
      if (summary?.requires_3ds !== false) {
        try {
          authData = await runPaymentLink3ds(attemptCard, cardholderName, {
            post3ds: (body) => postPaymentLink3ds(token, body),
            collectDeviceData: (url, jwt) => PortalApiService.collectDeviceData(url, jwt),
            challenge: openChallenge,
            setupReturnUrl: window.location.origin,
            stepUpReturnUrl: resolveThreeDSReturnUrl(),
          });
        } catch (error) {
          dispatch({ type: 'OUTCOME', outcome: classifyPaymentLinkError(error, '3ds') });
          return;
        }
      }
      dispatch({ type: 'STEP', step: 'charging' });
      try {
        const { status, envelope } = await payPaymentLink(token, buildPayBody(attemptCard, cardholderName, authData));
        const outcome = outcomeForPayResponse(status, envelope);
        if (outcome.kind === 'paid') {
          setFinishing(true);
          await new Promise((resolve) => window.setTimeout(resolve, FINISH_PAUSE_MS));
          dispatch({ type: 'LOADED', summary: outcome.summary, now: Date.now() });
        } else dispatch({ type: 'OUTCOME', outcome });
      } catch (error) {
        dispatch({ type: 'OUTCOME', outcome: classifyPaymentLinkError(error, 'pay') });
      }
    } finally {
      inFlight.current = false;
      setFinishing(false);
    }
  };

  // The browser names the saved PDF after the document title: "Receipt <business> <date>" while the print dialog is open.
  const printReceipt = () => {
    const previous = document.title;
    const restore = () => {
      document.title = previous;
      window.removeEventListener('afterprint', restore);
    };
    document.title = receiptDocumentTitle(t('receiptFileName'), summary?.business?.name || tenantName, summary?.receipt?.paid_at);
    window.addEventListener('afterprint', restore);
    window.print();
  };

  // ── Render ──────────────────────────────────────────────────────────────
  const remaining = msRemaining(state.deadline, now);
  const lowTime = remaining > 0 && remaining < 2 * 60 * 1000;
  const totalLabel = formatMoney(summary?.total, summary?.currency, lang);
  const displayName = summary?.business?.name || tenantName;

  return (
    <div className="paylink-page min-h-screen bg-[var(--surface)] text-ink-900" style={{ fontFamily: 'var(--font)' }}>
      <div className="mx-auto w-full max-w-[520px] px-4 pb-10 pt-4">
        <header className="mb-4 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            {logoUrl ? (
              <img src={logoUrl} alt="" className="h-10 w-10 flex-none rounded-[10px] border border-[var(--line)] bg-white object-contain" />
            ) : null}
            <div className="truncate text-[15px] font-semibold">{displayName}</div>
          </div>
          <div className="paylink-noprint"><LanguageToggle color="var(--tenant-primary, #111827)" /></div>
        </header>

        {state.phase === 'loading' && <Panel><p className="m-0 text-center text-[14px] text-ink-500">{t('loading')}</p></Panel>}

        {state.phase === 'loadError' && (
          <StatusPanel icon={<XCircle size={28} />} tone="bad" title={t('loadErrorTitle')} body={t('loadErrorBody')}>
            <Button variant="primary" className="mt-4 w-full justify-center" onClick={() => load()}>{t('tryAgain')}</Button>
          </StatusPanel>
        )}

        {state.phase === 'unavailable' && (
          <StatusPanel icon={<XCircle size={28} />} tone="bad" title={t('unavailableTitle')} body={t('unavailableBody')} />
        )}

        {state.phase === 'expired' && (
          <StatusPanel icon={<Hourglass size={28} />} tone="warn" title={t('expiredTitle')} body={t('expiredBody')} />
        )}

        {state.phase === 'locked' && (
          <StatusPanel icon={<Lock size={28} />} tone="bad" title={t('lockedTitle')} body={t('lockedBody')} />
        )}

        {state.phase === 'stopped' && (
          <StatusPanel icon={<XCircle size={28} />} tone="bad" title={t('stoppedTitle')} body={t(state.stoppedKey || 'err_GENERIC')} />
        )}

        {state.phase === 'polling' && (
          <StatusPanel icon={<Spinner />} tone="neutral" title={t('pollingTitle')} body={t('pollingBody')} />
        )}

        {state.phase === 'processing' && (
          <StatusPanel
            icon={<CheckCircle2 size={28} />}
            tone="ok"
            title={t('processingTitle')}
            body={state.notice?.key === 'stillConfirmingBody' ? t('stillConfirmingBody') : t('processingBody')}
          >
            {totalLabel ? (
              <dl className="m-0 mt-4 grid grid-cols-1 gap-2 text-left" data-testid="paylink-processing-amount">
                <ReceiptRow label={t('amountPaid')} value={totalLabel} />
              </dl>
            ) : null}
          </StatusPanel>
        )}

        {state.phase === 'paid' && summary && (
          <>
            <StatusPanel icon={<CheckCircle2 size={28} />} tone="ok" title={t('paidTitle')} body={t('paidBody')}>
              <dl className="m-0 mt-4 grid grid-cols-1 gap-2 text-left" data-testid="paylink-receipt">
                {summary.receipt?.confirmation_code ? <ReceiptRow label={t('confirmationCode')} value={summary.receipt.confirmation_code} mono big /> : null}
                <ReceiptRow label={t('amountPaid')} value={formatMoney(summary.receipt?.total ?? summary.total, summary.receipt?.currency ?? summary.currency, lang)} />
                <ReceiptRow label={t('authCode')} value={summary.receipt?.payment_auth_code || '—'} mono />
                {summary.receipt?.paid_at ? <ReceiptRow label={t('paidAt')} value={formatBusinessDateTime(summary.receipt.paid_at, lang)} /> : null}
              </dl>
              <Button variant="ghost" className="paylink-noprint mt-4 w-full justify-center !py-3 !text-[15px]" onClick={printReceipt} data-testid="paylink-download-receipt">
                {t('downloadReceipt')}
              </Button>
            </StatusPanel>
            <PaymentLinkPassCard pass={summary.pass} lang={lang} t={t} />
            <BookingSummary summary={summary} lang={lang} t={t} />
          </>
        )}

        {state.phase === 'submitting' && summary && (
          <PayingPanel step={state.step} done={finishing} slow={slow} summary={summary} totalLabel={totalLabel} lang={lang} t={t} />
        )}

        {state.phase === 'form' && summary && (
          <>
            <div
              className={`mb-3 flex items-center justify-between rounded-[12px] border px-4 py-3 ${
                lowTime ? 'border-[var(--warn-line)] bg-[var(--warn-bg-2)] text-[var(--warn-fg)]' : 'border-[var(--line)] bg-white'
              }`}
              role="timer"
              aria-live="off"
            >
              <div className="min-w-0">
                <div className="text-[13px] font-medium">
                  {summary.guests?.first_name ? t('greeting', { name: summary.guests.first_name }) : t('greetingAnon')}
                </div>
                <div className="text-[12px] text-ink-500">{t('holdHint')}</div>
              </div>
              <div className="ml-3 flex-none text-right">
                <div className="text-[11px] uppercase tracking-wide text-ink-500">{t('holdEndsIn')}</div>
                <div className="font-mono text-[22px] font-semibold tabular-nums" data-testid="paylink-countdown">{formatCountdown(remaining)}</div>
              </div>
            </div>

            <BookingSummary summary={summary} lang={lang} t={t} />

            <form className="mt-3 rounded-[12px] border border-[var(--line)] bg-white p-4" onSubmit={onPay} noValidate autoComplete="on">
              <h2 className="m-0 mb-1 text-[15px] font-semibold">{t('payWithCard')}</h2>
              <p className="m-0 mb-3 flex items-start gap-1.5 text-[12px] text-ink-500">
                <ShieldCheck size={14} className="mt-[1px] flex-none" />
                {t(summary.requires_3ds === false ? 'secureNoteNo3ds' : 'secureNote')}
              </p>

              {state.notice && (
                <Notice variant={state.notice.key === 'err_previousAttempt' ? 'warn' : 'error'} className="mb-3" data-testid="paylink-notice">
                  {t(state.notice.key)}
                  {typeof state.notice.attemptsRemaining === 'number' ? <> {t('err_attemptsRemaining', { n: state.notice.attemptsRemaining })}</> : null}
                </Notice>
              )}

              <CreditCardInput
                key={state.cardResetKey}
                onChange={setCard}
                labels={CARD_FORM_LABELS[lang]}
                brandOf={cardBrandOf}
              />

              <Button
                type="submit"
                variant="primary"
                className="mt-4 w-full justify-center !py-3 !text-[15px]"
                disabled={!cardComplete}
                data-testid="paylink-pay"
              >
                {t('payButton', { amount: totalLabel })}
              </Button>
            </form>
          </>
        )}
      </div>

      <ThreeDSModal
        isOpen={!!challenge}
        stepUpUrl={challenge?.stepUpUrl || ''}
        accessToken={challenge?.accessToken || ''}
        authTransactionId={challenge?.authTransactionId || ''}
        onAuthenticationComplete={onChallengeComplete}
        onError={onChallengeError}
      />
    </div>
  );
};

// ─── Pieces ────────────────────────────────────────────────────────────────

const Panel: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <section className={`rounded-[12px] border border-[var(--line)] bg-white p-4 ${className}`}>{children}</section>
);

const TONE: Record<'ok' | 'bad' | 'warn' | 'neutral', string> = {
  ok: 'text-[var(--ok-dot)] bg-[var(--ok-bg)]',
  bad: 'text-[var(--danger)] bg-[var(--bad-bg)]',
  warn: 'text-[var(--warn-fg)] bg-[var(--warn-bg)]',
  neutral: 'text-ink-700 bg-[var(--surface-2)]',
};

const StatusPanel: React.FC<{
  icon: React.ReactNode;
  tone: keyof typeof TONE;
  title: string;
  body: string;
  children?: React.ReactNode;
}> = ({ icon, tone, title, body, children }) => (
  <Panel className="mb-3 px-5 py-6 text-center">
    <div className={`mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full ${TONE[tone]}`}>{icon}</div>
    <h1 className="m-0 mb-1.5 text-[18px] font-semibold" data-testid="paylink-status-title">{title}</h1>
    <p className="m-0 text-[14px] leading-relaxed text-ink-500">{body}</p>
    {children}
  </Panel>
);

const ReceiptRow: React.FC<{ label: string; value: string; mono?: boolean; big?: boolean }> = ({ label, value, mono, big }) => (
  <div className="flex items-baseline justify-between gap-3 border-b border-[var(--line-subtle)] pb-2 last:border-b-0">
    <dt className="text-[13px] text-ink-500">{label}</dt>
    <dd className={`m-0 text-right ${mono ? 'font-mono' : ''} ${big ? 'text-[18px] font-semibold tracking-wider' : 'text-[14px] font-medium'}`}>{value}</dd>
  </div>
);

const Spinner = () => (
  <svg className="animate-spin" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" strokeOpacity="0.75" />
  </svg>
);

const BookingSummary: React.FC<{
  summary: PaymentLinkSummary;
  lang: 'en' | 'es';
  t: (key: PayLinkStringKey, vars?: Record<string, string | number>) => string;
}> = ({ summary, lang, t }) => {
  const items = summary.line_items || [];
  const serviceName = pickText(summary.service?.name, lang);
  const date = formatBusinessDate(summary.reservation_start, lang);
  const time = formatBusinessTimeRange(summary.reservation_start, summary.reservation_end, lang);
  return (
    <Panel>
      <h2 className="m-0 mb-3 text-[17px] font-semibold" data-testid="paylink-service">{serviceName}</h2>
      <ul className="m-0 mb-3 grid list-none grid-cols-1 gap-2 p-0 text-[14px]">
        {date && (
          <li className="flex items-center gap-2"><CalendarDays size={16} className="flex-none text-ink-500" /><span className="first-letter:uppercase">{date}</span></li>
        )}
        {time && (
          <li className="flex items-center gap-2"><Clock size={16} className="flex-none text-ink-500" />{time}</li>
        )}
        {typeof summary.guests?.total === 'number' && summary.guests.total > 0 && (
          <li className="flex items-center gap-2"><Users size={16} className="flex-none text-ink-500" />{t('guestsCount', { n: summary.guests.total })}</li>
        )}
      </ul>

      <div className="border-t border-[var(--line)] pt-3">
        <div className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-ink-500">{t('summary')}</div>
        <ul className="m-0 grid list-none grid-cols-1 gap-2 p-0" data-testid="paylink-line-items">
          {items.map((item, index) => {
            const label = pickText(item.label, lang);
            const currency = item.currency || summary.currency;
            const qty = Number(item.quantity);
            return (
              <li key={`${item.id ?? 'line'}-${index}`} className="flex items-start justify-between gap-3 text-[14px]">
                <div className="min-w-0">
                  <div className="font-medium">
                    {label}
                    {item.type === 'add_on' && (
                      <span className="ml-1.5 rounded-full bg-[var(--surface-2)] px-1.5 py-[1px] align-middle text-[11px] font-medium text-ink-500">{t('addOn')}</span>
                    )}
                  </div>
                  {Number.isFinite(qty) && qty > 0 && item.unit_price !== undefined && item.unit_price !== null && (
                    <div className="text-[12px] text-ink-500">{qty} × {formatMoney(item.unit_price, currency, lang)}</div>
                  )}
                </div>
                <div className="flex-none font-medium tabular-nums">{formatMoney(item.subtotal, currency, lang)}</div>
              </li>
            );
          })}
        </ul>
        <div className="mt-3 flex items-baseline justify-between border-t border-[var(--line)] pt-3">
          <span className="text-[15px] font-semibold">{t('total')}</span>
          <span className="text-[20px] font-bold tabular-nums" data-testid="paylink-total">{formatMoney(summary.total, summary.currency, lang)}</span>
        </div>
      </div>
    </Panel>
  );
};

export default PayLinkPage;
